/**
 * Converts a markdown string to a Quill text delta object
 *
 * Supports:
 * - Bold text: **text** -> { insert: "text", attributes: { bold: true } }
 * - Headers: # Header -> { insert: "Header\n", attributes: { header: 1 } }
 * - Headers: ## Header -> { insert: "Header\n", attributes: { header: 2 } }
 * - Headers: ### Header -> { insert: "Header\n", attributes: { header: 3 } }
 * - Links: [text](url) -> { insert: "text", attributes: { link: "url" } }
 * - Images: ![alt](url) -> { insert: { image: "url" } }
 *
 * @param {string} markdownText - The markdown string to convert
 * @returns {Object} - Quill delta object with ops array
 */
export const MARKDOWN_LINK_REGEX =
  /\[[^\]]+\]\([^\s)]+(?:\s+["'][^"']*["'])?\)/;
export const MARKDOWN_IMAGE_REGEX =
  /!\[[^\]]*\]\([^\s)]+(?:\s+["'][^"']*["'])?\)/;
export const MARKDOWN_HEADER_REGEX = /^#{1,3}\s/m;

export function convertMarkdownToTextDelta(markdownText) {
  if (!markdownText || typeof markdownText !== 'string') {
    return { ops: [{ insert: '\n' }] };
  }

  const ops = [];
  const lines = markdownText.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Process headers
    if (line.match(/^### /)) {
      const headerText = line.replace(/^### /, '');
      ops.push(...processLinksInOps(processBoldInLine(headerText)));
      ops.push({ insert: '\n', attributes: { header: 3 } });
    } else if (line.match(/^## /)) {
      const headerText = line.replace(/^## /, '');
      ops.push(...processLinksInOps(processBoldInLine(headerText)));
      ops.push({ insert: '\n', attributes: { header: 2 } });
    } else if (line.match(/^# /)) {
      const headerText = line.replace(/^# /, '');
      ops.push(...processLinksInOps(processBoldInLine(headerText)));
      ops.push({ insert: '\n', attributes: { header: 1 } });
    } else {
      // Process line with potential bold and link formatting
      const lineOps = processLinksInOps(processBoldInLine(line));
      ops.push(...lineOps);

      // Add newline if not last line
      if (i < lines.length - 1) {
        ops.push({ insert: '\n' });
      }
    }
  }

  return { ops };
}

/**
 * Processes bold formatting (**text**) within a line of text
 *
 * @param {string} line - The line of text to process
 * @returns {Array} - Array of delta operations for this line
 */
function processBoldInLine(line) {
  const ops = [];
  const boldPattern = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldPattern.exec(line)) !== null) {
    // Add text before bold
    if (match.index > lastIndex) {
      const beforeText = line.slice(lastIndex, match.index);
      if (beforeText) {
        ops.push({ insert: beforeText });
      }
    }

    // Add bold text
    ops.push({ insert: match[1], attributes: { bold: true } });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last bold pattern
  if (lastIndex > 0 && lastIndex < line.length) {
    ops.push({ insert: line.slice(lastIndex) });
  }

  // If no bold patterns found, add the whole line
  if (lastIndex === 0) {
    ops.push({ insert: line });
  }

  return ops;
}

/**
 * Processes link and image formatting within an array of ops.
 * - Links: [text](url) -> { insert: "text", attributes: { link: "url" } }
 * - Images: ![alt](url) -> { insert: { image: "url" } }
 * Runs after processBoldInLine so bold and link attributes can coexist.
 *
 * @param {Array} ops - Array of delta operations to process
 * @returns {Array} - Array of delta operations with links and images resolved
 */
function processLinksInOps(ops) {
  const result = [];
  // Captures: [1] optional '!', [2] alt/link text, [3] url (stops at whitespace/paren, ignores optional title)
  const pattern = /(!?)\[([^\]]*)\]\(([^\s)]+)(?:\s+["'][^"']*["'])?\)/g;

  for (const op of ops) {
    if (!op.insert || typeof op.insert !== 'string') {
      result.push(op);
      continue;
    }

    const text = op.insert;
    let lastIndex = 0;
    let match;
    pattern.lastIndex = 0;

    while ((match = pattern.exec(text)) !== null) {
      const [fullMatch, bang, altText, url] = match;

      if (match.index > lastIndex) {
        const before = text.slice(lastIndex, match.index);
        result.push(
          op.attributes
            ? { insert: before, attributes: op.attributes }
            : { insert: before },
        );
      }

      if (bang === '!') {
        result.push({ insert: { image: url } });
      } else {
        result.push({
          insert: altText,
          attributes: { ...(op.attributes || {}), link: url },
        });
      }

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex === 0) {
      result.push(op);
    } else if (lastIndex < text.length) {
      const remaining = text.slice(lastIndex);
      result.push(
        op.attributes
          ? { insert: remaining, attributes: op.attributes }
          : { insert: remaining },
      );
    }
  }

  return result;
}

/**
 * Checks if a string contains markdown syntax
 *
 * @param {string} text - The text to check
 * @returns {boolean} - True if markdown syntax is detected
 */
export function hasMarkdownSyntax(text) {
  if (!text || typeof text !== 'string') {
    return false;
  }

  return (
    text.includes('**') ||
    MARKDOWN_HEADER_REGEX.test(text) ||
    MARKDOWN_IMAGE_REGEX.test(text) ||
    MARKDOWN_LINK_REGEX.test(text)
  );
}

/**
 * Processes a text_delta object and converts any markdown content within it to formatted operations
 *
 * @param {Object} textDelta - The original text delta object with ops array
 * @returns {Object} - New text delta object with markdown content converted to formatting
 */
export function processMarkdownInTextDelta(textDelta) {
  if (!textDelta || !textDelta.ops || !Array.isArray(textDelta.ops)) {
    return { ops: [{ insert: '\n' }] };
  }

  const newOps = [];

  for (const op of textDelta.ops) {
    if (op.insert && typeof op.insert === 'string') {
      // Check if this operation contains markdown
      if (hasMarkdownSyntax(op.insert)) {
        // Convert the markdown text to delta operations
        const convertedDelta = convertMarkdownToTextDelta(op.insert);

        // If the original operation had attributes, we need to merge them
        if (op.attributes) {
          // Apply the original attributes to each converted operation
          const opsWithAttributes = convertedDelta.ops.map((convertedOp) => {
            if (convertedOp.attributes) {
              // Merge attributes, with converted attributes taking precedence
              return {
                ...convertedOp,
                attributes: { ...op.attributes, ...convertedOp.attributes },
              };
            } else if (
              convertedOp.insert !== '\n' ||
              !Object.keys(op.attributes).some((key) =>
                ['header', 'blockquote', 'code-block', 'list'].includes(key),
              )
            ) {
              // Apply original attributes to text, but not to newlines if original had block-level attributes
              return {
                ...convertedOp,
                attributes: op.attributes,
              };
            }
            return convertedOp;
          });
          newOps.push(...opsWithAttributes);
        } else {
          // No original attributes, just add the converted operations
          newOps.push(...convertedDelta.ops);
        }
      } else {
        // No markdown, keep the original operation
        newOps.push(op);
      }
    } else {
      // Not a string insert (could be embed, etc.), keep as-is
      newOps.push(op);
    }
  }

  return { ops: newOps };
}

export default convertMarkdownToTextDelta;
